import React, { useEffect, useState } from 'react'
import { NavLink, useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { formatDateTime } from 'util/masks'
import { ApplicationState } from 'AppReducer'
import { deleteProduct, getProductList } from 'panel/product/redux/actions'

import Pagination from 'util/modules/Pagination'
import Select from 'util/modules/Form/Select'

type Filter = { name: string, category: string, isSellable?: boolean, isActive?: boolean }
const initialFilters: Filter = { name: '', category: '' }

const ProductList: React.FC = () => {
  const dispatch = useDispatch()
  const { partnerId } = useParams<{ [x: string]: string }>()

  const { productList: { items, pagination } } = useSelector((state: ApplicationState) => state.productReducer)
  const { categoryList: { items: categories } } = useSelector((state: ApplicationState) => state.categoryReducer)

  const history = useHistory()
  const { location: { search } } = history

  const [filter, setFilter] = useState(initialFilters)

  function handleFetch (search: string): void {
    const query = new URLSearchParams(search)

    const urlQuery = Object.fromEntries(query.entries())

    const defaultQuery = {
      page: 1,
      limit: 10,
      fields: 'id,pictureTiny,isSellable,name,c.name,createdAt,updatedAt',
      'sort[isSellable]': 'ASC'
    }

    getProductList(partnerId, query.get('page') ? urlQuery : defaultQuery)(dispatch)
  }

  function handleFilter (e: React.FormEvent): void {
    e.preventDefault()
    const query: any = {
      page: 1,
      limit: 10,
      fields: 'id,pictureTiny,isSellable,name,c.id,c.name,createdAt,updatedAt',
      'sort[isSellable]': 'ASC'
    }

    if (filter.name) {
      query['filter_like[name]'] = `^${filter.name}`
    }
    if (filter.category) {
      query['filter[c.id]'] = filter.category
    }
    if (filter.isActive !== undefined) {
      query['filter[isActive]'] = `${filter.isActive ? 1 : 0}`
    }
    if (filter.isSellable !== undefined) {
      query['filter[isSellable]'] = `${filter.isSellable ? 1 : 0}`
    }
    getProductList(partnerId, query)(dispatch)

    history.push(`/panel/partner/${partnerId}/product?${(new URLSearchParams(query)).toString()}`)
  }

  function handleRemove (id: number): void {
    if (window.confirm('Tem certeza que deseja remover?')) {
      deleteProduct(partnerId, id)(dispatch).then(res => {
        if (res?.status === 'success') {
          history.push(`/panel/partner/${partnerId}/product`)
        }
      })
    }
  }

  useEffect(() => {
    handleFetch(search)
    // eslint-disable-next-line
  }, [search])

  const gridTemplateColumns = '60px 1fr 300px 200px 200px 310px'
  const defaultPicture = `${process.env.PUBLIC_URL}/img/default-img.png`

  return (
    <div className="row justify-start margin-top-32 ">
      <form
        onSubmit={handleFilter}
        className="row panel margin-bottom-16 align-end"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 200px 200px 48px' }}
      >
        <div className="form-control">
          <label>Filtrar por nome</label>
          <input
            type="text"
            value={filter.name}
            onChange={(e): void => setFilter({ ...filter, name: e.target.value })}
          />
        </div>
        <div className="form-control">
          <label>Filtrar por Categoria</label>
          <Select
            selected={String(filter.category)}
            onSelect={(key): void => setFilter({ ...filter, category: key })}
            itens={categories.map(({ id, name }) => ({ key: id, value: name }))}
          />
        </div>
        <div className="form-control">
          <label>Status</label>
          <select
            value={filter.isActive ? 1 : (filter.isActive === false ? 0 : undefined)}
            onChange={(e): void => {
              const value = parseInt(e.target.value)
              setFilter({ ...filter, isActive: value === 1 ? true : (value === 0 ? false : undefined) })
            }}
          >
            <option>Todos</option>
            <option value={1}>Ativos</option>
            <option value={0}>Inativos</option>
          </select>
        </div>
        <div className="form-control">
          <label>Tipo</label>
          <select
            value={filter.isSellable ? 1 : (filter.isSellable === false ? 0 : undefined)}
            onChange={(e): void => {
              const value = parseInt(e.target.value)
              setFilter({ ...filter, isSellable: value === 1 ? true : (value === 0 ? false : undefined) })
            }}
          >
            <option>Todos</option>
            <option value={1}>Vendáveis</option>
            <option value={0}>Não Vendáveis</option>
          </select>
        </div>

        <button className="col-12 primary"><i className="fa fa-filter" style={{ margin: 0 }} /></button>
      </form>

      <h2 className="margin-top-32"><i className="fa fa-history"/> Disponíveis para venda</h2>
      <div className="list">
        <div className="list-header" style={{ display: 'grid', gridTemplateColumns }}>
          <div className="hidden-mobile" />
          <div>Nome</div>
          <div>Categoria</div>
          <div>Criado em</div>
          <div>Atualizado em</div>
          <div />
        </div>
        {
          items.filter(item => item.isSellable).length === 0 &&
          <div className="list-row">
            <div className="grid-span">
              Nenhum produto cadastrado
            </div>
          </div>
        }

        {
          items.filter(item => item.isSellable).map(item => (
            <div className="list-row" key={item.id} style={{ display: 'grid', gridTemplateColumns }}>
              <div style={{ width: 50, height: 50 }} className="hidden-mobile">
                <img src={item.pictureTiny || defaultPicture} className="col-12 border-radius" alt="" />
              </div>

              <div>{ item.name }</div>
              <div>{ item.category?.name }</div>
              <div>{ formatDateTime(item.createdAt) }</div>
              <div>{ formatDateTime(item.updatedAt, '...') }</div>
              <div className="row">
                <span
                  onClick={(): void => handleRemove(item.id || 0)}
                  className="button remove"
                  style={{ width: 48 }}
                >
                  <i className="fa fa-trash"/>
                </span>
                <NavLink
                  style={{ width: 142 }}
                  className="button secondary margin-lr-8"
                  to={`/panel/partner/${partnerId}/product/${item.id}/config`}
                >
                  <i className="fa fa-cogs"/> Configurar
                </NavLink>
                <NavLink
                  style={{ width: 100 }}
                  className="button primary"
                  to={`/panel/partner/${partnerId}/product/${item.id}${search}`}
                >
                  Editar
                </NavLink>
              </div>
            </div>
          ))
        }
      </div>

      <h2 className="margin-top-32"><i className="fa fa-history"/> Produtos complementares</h2>
      <div className="list">
        <div className="list-header" style={{ display: 'grid', gridTemplateColumns }}>
          <div className="hidden-mobile" />
          <div>Nome</div>
          <div>Categoria</div>
          <div>Criado em</div>
          <div>Atualizado em</div>
          <div />
        </div>
        {
          items.filter(item => !item.isSellable).length === 0 &&
          <div className="list-row">
            <div className="grid-span">
              Nenhum produto cadastrado
            </div>
          </div>
        }

        {
          items.filter(item => !item.isSellable).map(item => (
            <div className="list-row" key={item.id} style={{ display: 'grid', gridTemplateColumns }}>
              <div style={{ width: 50, height: 50 }} className="hidden-mobile">
                <img src={item.pictureTiny || defaultPicture} className="col-12 border-radius" alt="" />
              </div>

              <div>{ item.name }</div>
              <div>{ item.category?.name }</div>
              <div>{ formatDateTime(item.createdAt) }</div>
              <div>{ formatDateTime(item.updatedAt, '...') }</div>
              <div className="row">
                <span
                  onClick={(): void => handleRemove(item.id || 0)}
                  className="button remove"
                  style={{ width: 48 }}
                >
                  <i className="fa fa-trash"/>
                </span>
                <NavLink
                  style={{ width: 142 }}
                  className="button secondary margin-lr-8"
                  to={`/panel/partner/${partnerId}/product/${item.id}/config`}
                >
                  <i className="fa fa-cogs"/> Configurar
                </NavLink>
                <NavLink
                  style={{ width: 100 }}
                  className="button primary"
                  to={`/panel/partner/${partnerId}/product/${item.id}${search}`}
                >
                  Editar
                </NavLink>
              </div>
            </div>
          ))
        }
      </div>

      <Pagination pagination={pagination} />
    </div>
  )
}

export default ProductList
